import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle } from 'react-feather';
import { Badge, Button, Card, Col, Form, ListGroup, Row, Tab, Tabs } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { forceSyncDirectoryAPI, forceSyncUserAPI, searchDirectoryAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import { companyUrl } from '../../../utils/mhNav';
import styles from './DirectoryExplorer.module.scss';

function useDebouncedValue(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function DirectoryExplorer() {
  const [q, setQ] = useState('');
  const debounced = useDebouncedValue(q, 300);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [syncOpen, setSyncOpen] = useState(false);

  const runSearch = useCallback(async (query) => {
    setLoading(true);
    setError(null);
    try {
      const res = await searchDirectoryAPI(query);
      setData(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    runSearch(debounced);
  }, [debounced, runSearch]);

  const people = data?.people || [];
  const groups = data?.groups || [];
  const externals = data?.externals || [];
  const hasResults = people.length + groups.length + externals.length > 0;

  const initials = (name) =>
    String(name || '?')
      .split(' ')
      .map((s) => s[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();

  const emptyQuery = !debounced.trim();

  return (
    <div>
      <PageHeader
        title="Directory explorer"
        breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Directory' }]}
        secondaryActions={[{ label: 'Force sync', onClick: () => setSyncOpen(true), variant: 'outline-primary' }]}
      />

      <Form.Control
        className={`mb-3 ${styles.searchLg}`}
        placeholder="Search people, groups, emails…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />

      {error && (
        <EmptyState icon={AlertTriangle} title="Search failed" description={error.message} actionLabel="Retry" onAction={() => runSearch(debounced)} />
      )}

      {loading && !data && <LoadingSkeleton rows={4} cols={2} />}

      {!loading && !error && !emptyQuery && !hasResults && (
        <EmptyState
          icon={AlertTriangle}
          title="No results found"
          description="Create external contact?"
          actionLabel="Add external (mock)"
          onAction={() => toast.info('Opens contact form (mock)')}
        />
      )}

      {!emptyQuery && hasResults && (
        <div className={styles.resultsLayout}>
          <div className={styles.mainCol}>
            <Tabs defaultActiveKey="people" className="mb-3">
              <Tab eventKey="people" title={`People (${people.length})`}>
                <Row className="g-2">
                  {people.map((p) => (
                    <Col md={6} lg={4} key={p.id}>
                      <div
                        role="button"
                        tabIndex={0}
                        className={styles.personCard}
                        onClick={() => setSelected({ kind: 'person', ...p })}
                        onKeyDown={(e) => e.key === 'Enter' && setSelected({ kind: 'person', ...p })}
                      >
                        <div className="d-flex gap-2">
                          <div className={styles.avatarSm}>{initials(p.displayName)}</div>
                          <div>
                            <div className="fw-medium">{p.displayName}</div>
                            <div className="small text-muted">{p.email}</div>
                            <Badge bg="light" text="dark" className="mt-1">
                              {p.department}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Tab>
              <Tab eventKey="groups" title={`Groups (${groups.length})`}>
                <ListGroup>
                  {groups.map((g) => (
                    <ListGroup.Item key={g.id} action onClick={() => setSelected({ kind: 'group', ...g })}>
                      <div className="fw-medium">{g.displayName}</div>
                      <div className="small">
                        <Badge bg="secondary" className="me-2">
                          {g.provider}
                        </Badge>
                        {g.memberCount} members —{' '}
                        <Button variant="link" size="sm" className="p-0 align-baseline">
                          View members
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Tab>
              <Tab eventKey="externals" title={`Externals (${externals.length})`}>
                <ListGroup>
                  {externals.map((x) => (
                    <ListGroup.Item key={x.id} action onClick={() => setSelected({ kind: 'external', ...x })}>
                      <div>{x.email}</div>
                      <div className="small text-muted">
                        Added by {x.addedBy} · Last meeting {new Date(x.lastMeetingAt).toLocaleDateString()}
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Tab>
            </Tabs>
          </div>

          <aside className={styles.sideCol}>
            {!selected ? (
              <p className="text-muted small mb-0">Select a result</p>
            ) : (
              <>
                <h3 className="h6">{selected.displayName || selected.email}</h3>
                <p className="small mb-1">{selected.email}</p>
                {selected.department && <p className="small">Dept: {selected.department}</p>}
                <div className="mb-2">
                  {(selected.sourceSystems || [selected.provider].filter(Boolean)).map((s) => (
                    <Badge key={s} bg="info" className="me-1">
                      {s}
                    </Badge>
                  ))}
                </div>
                <p className="small fw-medium mb-1">Aliases</p>
                <ul className="small">
                  {(selected.aliases || []).map((a) => (
                    <li key={a}>{a}</li>
                  ))}
                </ul>
                <p className="small fw-medium mb-1">Recent meetings</p>
                <ul className="small">
                  {(selected.recentMeetings || []).map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={async () => {
                    try {
                      await forceSyncUserAPI(selected.id);
                      toast.success('User sync queued');
                    } catch (e) {
                      toast.error(e.message);
                    }
                  }}
                >
                  Force sync this user
                </Button>
              </>
            )}
          </aside>
        </div>
      )}

      {emptyQuery && !loading && (
        <Card>
          <Card.Body className="text-muted small">Start typing to search the directory.</Card.Body>
        </Card>
      )}

      <ConfirmModal
        isOpen={syncOpen}
        onClose={() => setSyncOpen(false)}
        onConfirm={async () => {
          try {
            const res = await forceSyncDirectoryAPI();
            toast.success(`Synced ${res.data?.synced ?? 0} records (mock)`);
            setSyncOpen(false);
            runSearch(debounced);
          } catch (e) {
            toast.error(e.message);
          }
        }}
        title="Force full directory sync?"
        message="This may take a few minutes (mock)."
        confirmLabel="Sync"
      />
    </div>
  );
}
